import { z } from 'zod';
/**
 * Sign-in contracts and the role rules behind them (section 2).
 *
 * Students have no email or phone by design (section 10), so they sign in with
 * school code + roll number + PIN. Staff sign in with email and password.
 */
export const CREDENTIALS = {
    /** Student PIN: short enough to remember, throttled hard to compensate. */
    pinLength: 4,
    staffPasswordMinLength: 12,
    /** Consecutive failures before the account is locked. */
    maxFailedSignIns: 5,
    lockoutMinutes: 15,
    /** A shared classroom device should not stay signed in overnight. */
    studentSessionHours: 12,
    staffSessionDays: 30,
};
export const schoolCodeSchema = z
    .string()
    .trim()
    .min(3)
    .max(32)
    .regex(/^[A-Za-z0-9-]+$/, 'A school code uses letters, numbers and hyphens only.');
/**
 * Roll numbers are stored zero-padded to three digits (`001`), the way a class
 * register prints them, and a student types `1`. An all-digit roll is
 * normalized to that form — leading zeros dropped, then padded back to three —
 * so `1`, `01` and `001` are one student, and `1234` stays `1234`. Anything
 * with a letter in it is left exactly as typed: a school that numbers `A12`
 * meant `A12`.
 */
export const ROLL_NO_DIGITS = 3;
export function normalizeRollNo(rollNo) {
    const trimmed = rollNo.trim();
    if (!/^\d+$/.test(trimmed))
        return trimmed;
    return trimmed.replace(/^0+(?=\d)/, '').padStart(ROLL_NO_DIGITS, '0');
}
export const rollNoSchema = z.string().trim().min(1).max(16).transform(normalizeRollNo);
export const pinSchema = z
    .string()
    .trim()
    .regex(new RegExp(`^\\d{${CREDENTIALS.pinLength}}$`), `A PIN is ${CREDENTIALS.pinLength} digits.`);
export const staffPasswordSchema = z
    .string()
    .min(CREDENTIALS.staffPasswordMinLength, `Use at least ${CREDENTIALS.staffPasswordMinLength} characters.`)
    .max(256);
export const studentLoginSchema = z.object({
    schoolCode: schoolCodeSchema,
    rollNo: rollNoSchema,
    pin: pinSchema,
});
export const staffLoginSchema = z.object({
    email: z.string().trim().email().max(254),
    password: z.string().min(1).max(256),
});
export const changeCredentialSchema = z.object({
    current: z.string().min(1).max(256),
    next: z.string().min(1).max(256),
});
/** What the client is told about the signed-in person. Never more than this. */
export const sessionUserSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    role: z.enum(['student', 'teacher', 'admin']),
    schoolId: z.string().uuid(),
    schoolName: z.string(),
    /** The school's board, for anything that names the exam ("AP SSC Class 10"). */
    board: z.object({ code: z.string(), name: z.string(), grade: z.number().int() }),
    rollNo: z.string().nullable(),
    /** The client routes to the change-credential screen when this is true. */
    mustChangeCredential: z.boolean(),
    /** An admin who may also manage every school on the server (migration 0010). */
    operator: z.boolean(),
    classes: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
});
/**
 * Role rules from the table in section 2, in one place so a route and the UI
 * cannot disagree about who may do what.
 */
export const ROLE_RULES = {
    /** Release topics to a class and set levels per student. */
    manageClass: (role) => role === 'teacher' || role === 'admin',
    /** Approve or dismiss AI level suggestions. */
    decideSuggestions: (role) => role === 'teacher' || role === 'admin',
    /** Read class-wide and other students' data. */
    readClassData: (role) => role === 'teacher' || role === 'admin',
    /** Edit syllabus, papers and tags. Admin only — teachers never edit content. */
    editContent: (role) => role === 'admin',
    /** Create schools, classes and users; see cost and usage. */
    administer: (role) => role === 'admin',
    /** Learn, practice and read one's own progress. */
    learn: (role) => role === 'student',
};
/** Messages the sign-in screen shows. American English, never blaming. */
export const AUTH_MESSAGES = {
    invalidCredentials: 'That sign-in did not match. Check the details and try again.',
    locked: 'Too many tries. Wait a few minutes, or ask your teacher to reset your PIN.',
    inactive: 'This account is not active. Ask your teacher or the school office.',
    signedOut: 'You are signed out.',
    sessionExpired: 'You were signed out. Please sign in again.',
    mustChangeCredential: 'Choose a new PIN or password before going on.',
};
//# sourceMappingURL=auth.js.map